import { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import {
  Briefcase, MapPin, ArrowLeft, PencilSimple, Trash, ArrowSquareOut,
  Envelope, Phone, BookmarkSimple, Buildings, GlobeHemisphereEast, MagnifyingGlass,
} from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import UserLink from "../components/UserLink";
import {
  Header, ScopeTabs, Loading, Empty, NotFound,
  FormField, FormModal, inputCls,
} from "./MeetupsPage";

/**
 * JobsPage — hiring board.
 * /app/jobs        → list + search + remote filter + create
 * /app/jobs/:jid   → detail with apply/contact + save
 */
export default function JobsPage() {
  const { jid } = useParams();
  return jid ? <JobDetail jid={jid}/> : <JobsList/>;
}


function JobsList() {
  const [items, setItems] = useState([]);
  const [scope, setScope] = useState("open");
  const [remoteOnly, setRemoteOnly] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [urlParams, setUrlParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(urlParams.get("new") === "1");
  const navigate = useNavigate();

  useEffect(() => {
    if (urlParams.get("new") === "1") {
      const next = new URLSearchParams(urlParams);
      next.delete("new");
      setUrlParams(next, { replace: true });
    }
  }, [urlParams, setUrlParams]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { scope };
      if (remoteOnly) params.remote = true;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/jobs", { params });
      setItems(data || []);
    } catch { setItems([]); }
    setLoading(false);
  };
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [scope, remoteOnly, q]);

  const create = async (payload) => {
    const { data } = await api.post("/jobs", payload);
    setCreateOpen(false);
    navigate(`/app/jobs/${data.job_id}`);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="jobs-list">
      <Header title="Jobs" sub="Command Center · Jobs" actionLabel="Post Job"
              onAction={() => setCreateOpen(true)} testid="jobs"/>

      <div className="flex items-center gap-3 flex-wrap">
        <ScopeTabs scope={scope} onChange={setScope}
                   options={[["open","Open"],["saved","Saved"],["mine","Mine"]]}
                   testid="jobs-scope"/>
        <button onClick={() => setRemoteOnly(!remoteOnly)} data-testid="jobs-remote-toggle"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[10px] uppercase tracking-widest"
                style={remoteOnly
                  ? { background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }
                  : { border: "1px solid rgba(212,175,55,0.4)", color: "#F2DC8C" }}>
          <GlobeHemisphereEast size={11}/> Remote only
        </button>
        <div className="flex-1 min-w-[220px] max-w-md rounded-lg flex items-center gap-2 px-3"
             style={{ border: "1px solid rgba(212,175,55,0.4)", background: "rgba(0,0,0,0.4)" }}>
          <MagnifyingGlass size={13} className="text-[#FFD700]"/>
          <input data-testid="jobs-search" value={q}
                 onChange={(e) => setQ(e.target.value)}
                 placeholder="Search title, company, description"
                 className="flex-1 bg-transparent py-2 text-[13px] text-[#F2DC8C] placeholder:text-[#F2DC8C]/40 focus:outline-none"/>
        </div>
      </div>

      {loading ? <Loading/>
        : items.length === 0 ? <Empty label="No matching openings." onCreate={() => setCreateOpen(true)} cta="Post a Job" icon={Briefcase}/>
        : (
          <div className="space-y-3" data-testid="jobs-listing">
            {items.map((j) => <JobRow key={j.job_id} job={j}/>)}
          </div>
        )}

      <AnimatePresence>
        {createOpen && <JobModal onClose={() => setCreateOpen(false)} onSave={create}/>}
      </AnimatePresence>
    </div>
  );
}


function JobRow({ job }) {
  return (
    <Link to={`/app/jobs/${job.job_id}`} data-testid={`job-row-${job.job_id}`}
          className="block rounded-2xl p-4 transition-all hover:-translate-y-0.5"
          style={{
            background: "linear-gradient(180deg, rgba(26,18,6,0.75) 0%, rgba(5,3,0,0.9) 100%)",
            border: "1px solid rgba(212,175,55,0.45)",
          }}>
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)" }}>
          <Briefcase size={16} weight="fill" color="#0a0703"/>
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-display text-[17px] text-[#FFD700] truncate">{job.title}</div>
          <div className="text-[11px] text-[#F2DC8C]/80 flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="inline-flex items-center gap-1"><Buildings size={11}/> {job.company}</span>
            {job.location && (
              <span className="inline-flex items-center gap-1"><MapPin size={11}/> {job.location}</span>
            )}
            {job.remote && (
              <span className="inline-flex items-center gap-1 text-[#FFD700]"><GlobeHemisphereEast size={11}/> Remote</span>
            )}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#F2DC8C]/55 mt-1">
            {job.salary && <span className="text-[#FFD700]">{job.salary} · </span>}
            Posted {new Date(job.created_at).toLocaleDateString()} · <UserLink username={job.owner_username}>@{job.owner_username}</UserLink>
          </div>
        </div>
        {job.is_saved && <BookmarkSimple size={13} weight="fill" className="text-[#FFD700]"/>}
      </div>
    </Link>
  );
}


function JobDetail({ jid }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [j, setJ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/jobs/${jid}`); setJ(data); } catch { setJ(null); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [jid]);

  const isOwner = j && user && j.owner_id === user.user_id;
  const toggleSave = async () => {
    const { data } = await api.post(`/jobs/${jid}/save`);
    setJ((p) => p && { ...p, is_saved: data.is_saved });
  };
  const remove = async () => {
    if (!window.confirm("Delete this job posting?")) return;
    await api.delete(`/jobs/${jid}`);
    navigate("/app/jobs");
  };
  const save = async (payload) => {
    await api.put(`/jobs/${jid}`, payload); setEditOpen(false); load();
  };

  if (loading) return <Loading/>;
  if (!j) return <NotFound label="Job not found" backTo="/app/jobs"/>;

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="job-detail">
      <Link to="/app/jobs" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#F2DC8C]/70 hover:text-[#FFD700]">
        <ArrowLeft size={12}/> All Jobs
      </Link>
      <div className="rounded-2xl p-6"
           style={{
             background: "linear-gradient(180deg, rgba(26,18,6,0.85) 0%, rgba(5,3,0,0.95) 100%)",
             border: "1px solid rgba(255,215,0,0.4)",
             boxShadow: "0 12px 40px rgba(0,0,0,0.55)",
           }}>
        <div className="flex items-start gap-3">
          <span className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
                style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)" }}>
            <Briefcase size={22} weight="fill" color="#0a0703"/>
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-[26px] leading-tight"
                style={{
                  background: "linear-gradient(180deg, #FFF3AE 0%, #FFD700 45%, #8a6a00 100%)",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                }}>
              {j.title}
            </h1>
            <div className="mt-1 text-[13px] text-[#F2DC8C]/85 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1"><Buildings size={12}/> {j.company}</span>
              {j.location && <span className="inline-flex items-center gap-1"><MapPin size={12}/> {j.location}</span>}
              {j.remote && <span className="inline-flex items-center gap-1 text-[#FFD700]"><GlobeHemisphereEast size={12}/> Remote</span>}
            </div>
            {j.salary && (
              <div className="mt-2 inline-block px-2 py-1 rounded-md text-[11px] uppercase tracking-widest"
                   style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
                {j.salary}
              </div>
            )}
          </div>
        </div>
        {j.description && (
          <p className="mt-5 text-[13px] text-[#F2DC8C] leading-relaxed whitespace-pre-wrap">
            {j.description}
          </p>
        )}

        <div className="mt-6 flex items-center gap-2 flex-wrap">
          {j.apply_url && (
            <a href={j.apply_url} target="_blank" rel="noopener noreferrer" data-testid="job-apply"
               className="inline-flex items-center gap-2 h-11 px-6 rounded-xl font-display uppercase text-[12px] tracking-[0.22em]"
               style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703",
                        boxShadow: "0 6px 20px rgba(255,215,0,0.35)" }}>
              <ArrowSquareOut size={12}/> Easy Apply
            </a>
          )}
          {j.contact_email && (
            <a href={`mailto:${j.contact_email}`} data-testid="job-email"
               className="inline-flex items-center gap-1.5 h-11 px-4 rounded-xl text-[11px] uppercase tracking-widest text-[#F2DC8C]"
               style={{ border: "1px solid rgba(212,175,55,0.55)" }}>
              <Envelope size={11}/> Email
            </a>
          )}
          {j.contact_phone && (
            <a href={`tel:${j.contact_phone}`} data-testid="job-phone"
               className="inline-flex items-center gap-1.5 h-11 px-4 rounded-xl text-[11px] uppercase tracking-widest text-[#F2DC8C]"
               style={{ border: "1px solid rgba(212,175,55,0.55)" }}>
              <Phone size={11}/> Call
            </a>
          )}
          <button onClick={toggleSave} data-testid="job-save"
                  className="inline-flex items-center gap-1.5 h-11 px-4 rounded-xl text-[11px] uppercase tracking-widest"
                  style={j.is_saved
                    ? { background: "rgba(255,215,0,0.10)", border: "1px solid rgba(255,215,0,0.55)", color: "#FFD700" }
                    : { border: "1px solid rgba(212,175,55,0.55)", color: "#F2DC8C" }}>
            <BookmarkSimple size={11} weight={j.is_saved ? "fill" : "regular"}/>
            {j.is_saved ? "Saved" : "Save"}
          </button>
          {isOwner && (
            <>
              <button onClick={() => setEditOpen(true)} data-testid="job-edit"
                      className="inline-flex items-center gap-1.5 h-11 px-4 rounded-xl text-[11px] uppercase tracking-widest"
                      style={{ border: "1px solid rgba(212,175,55,0.55)", color: "#F2DC8C" }}>
                <PencilSimple size={11}/> Edit
              </button>
              <button onClick={remove} data-testid="job-delete"
                      className="inline-flex items-center gap-1.5 h-11 px-4 rounded-xl text-[11px] uppercase tracking-widest text-[#FF5D73]"
                      style={{ border: "1px solid rgba(255,93,115,0.4)" }}>
                <Trash size={11}/> Delete
              </button>
            </>
          )}
        </div>
      </div>

      <AnimatePresence>
        {editOpen && <JobModal job={j} onClose={() => setEditOpen(false)} onSave={save}/>}
      </AnimatePresence>
    </div>
  );
}


function JobModal({ job, onClose, onSave }) {
  const [title, setTitle] = useState(job?.title || "");
  const [company, setCompany] = useState(job?.company || "");
  const [location, setLocation] = useState(job?.location || "");
  const [description, setDescription] = useState(job?.description || "");
  const [salary, setSalary] = useState(job?.salary || "");
  const [applyUrl, setApplyUrl] = useState(job?.apply_url || "");
  const [email, setEmail] = useState(job?.contact_email || "");
  const [phone, setPhone] = useState(job?.contact_phone || "");
  const [remote, setRemote] = useState(job?.remote || false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!title.trim() || !company.trim()) return;
    setBusy(true);
    try {
      await onSave({
        title, company, location: location || null, description: description || null,
        salary: salary || null, apply_url: applyUrl || null,
        contact_email: email || null, contact_phone: phone || null, remote,
      });
    } finally { setBusy(false); }
  };

  return (
    <FormModal title={job ? "Edit Job" : "Post Job"} icon={Briefcase}
               onClose={onClose} onSubmit={submit} busy={busy}
               disabled={!title.trim() || !company.trim()}
               saveLabel={job ? "Save" : "Post Job"} testid="job-modal">
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Title">
          <input autoFocus data-testid="job-title" value={title}
                 onChange={(e) => setTitle(e.target.value.slice(0, 180))}
                 placeholder="Senior Barista" className={inputCls}/>
        </FormField>
        <FormField label="Company">
          <input data-testid="job-company" value={company}
                 onChange={(e) => setCompany(e.target.value.slice(0, 140))}
                 placeholder="Aloha Coffee" className={inputCls}/>
        </FormField>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Location (optional)">
          <input data-testid="job-loc" value={location}
                 onChange={(e) => setLocation(e.target.value.slice(0, 200))}
                 placeholder="Lahaina, Maui" className={inputCls}/>
        </FormField>
        <FormField label="Salary (optional)">
          <input data-testid="job-salary" value={salary}
                 onChange={(e) => setSalary(e.target.value.slice(0, 80))}
                 placeholder="$25-30/hr" className={inputCls}/>
        </FormField>
      </div>
      <FormField label="Remote?">
        <button type="button" onClick={() => setRemote(!remote)}
                data-testid="job-remote"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-widest"
                style={remote
                  ? { background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }
                  : { border: "1px solid rgba(212,175,55,0.45)", color: "#F2DC8C" }}>
          <GlobeHemisphereEast size={10}/> {remote ? "Remote-friendly" : "On-site only"}
        </button>
      </FormField>
      <FormField label="Description (optional)">
        <textarea data-testid="job-desc" value={description} rows={5}
                  onChange={(e) => setDescription(e.target.value.slice(0, 12000))}
                  placeholder="Role, responsibilities, requirements…"
                  className={inputCls + " resize-none"}/>
      </FormField>
      <FormField label="Apply URL (optional)">
        <input data-testid="job-apply" value={applyUrl}
               onChange={(e) => setApplyUrl(e.target.value.slice(0, 600))}
               placeholder="https://…" className={inputCls}/>
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Contact email (optional)">
          <input data-testid="job-email" value={email}
                 onChange={(e) => setEmail(e.target.value.slice(0, 200))}
                 placeholder="hiring@example.com" className={inputCls}/>
        </FormField>
        <FormField label="Contact phone (optional)">
          <input data-testid="job-phone" value={phone}
                 onChange={(e) => setPhone(e.target.value.slice(0, 60))}
                 placeholder="808-555-1234" className={inputCls}/>
        </FormField>
      </div>
    </FormModal>
  );
}
